/**
 * Cued-blink metronome.
 *
 * The point of this is latency. A keytap label trails the blink it marks by the
 * labeller's reaction time, which makes it fine for asking "did a blink happen?"
 * and useless for asking "how long did we take to notice?". A beat the subject
 * blinks *on* inverts that: the cue time is known exactly, so the gap between
 * cue and detection is measurable.
 *
 * Neither mode is sufficient alone — the metronome says nothing about false
 * positives during natural use, which is exactly what a keytap session covers.
 */
export class Metronome {
  #timer: ReturnType<typeof setInterval> | null = null;
  #ctx: AudioContext | null = null;

  constructor(
    private readonly intervalMs: number,
    private readonly onBeat: () => void,
  ) {}

  get running(): boolean {
    return this.#timer !== null;
  }

  start(): void {
    if (this.#timer) return;
    this.#ctx ??= new AudioContext();
    this.#timer = setInterval(() => {
      this.#click();
      this.onBeat();
    }, this.intervalMs);
  }

  stop(): void {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }

  #click(): void {
    const ctx = this.#ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.06);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.07);
  }
}
